import React from 'react';
import { motion } from 'framer-motion';
import { PhoneOff, Mic, MicOff, Video as VideoIcon, VideoOff } from 'lucide-react';
import { AdvisorAvatar } from './AdvisorAvatar';

interface VideoCallInterfaceProps {
  show: boolean;
  advisorName: string;
  onEndCall: () => void;
}

export const VideoCallInterface: React.FC<VideoCallInterfaceProps> = ({
  show,
  advisorName,
  onEndCall
}) => {
  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-50 flex flex-col"
    >
      {/* Video Call Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 bg-black/50 backdrop-blur-sm border-b border-white/10">
        <h2 className="text-lg sm:text-xl font-bold text-white">
          Video Call with {advisorName}
        </h2>
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          Live
        </div>
      </div>

      {/* Video Area */}
      <div className="flex-1 relative bg-gradient-to-br from-gray-900 to-black">
        {/* Advisor Video (Placeholder) */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <AdvisorAvatar name={advisorName} size="large" className="mx-auto mb-6 w-24 h-24 sm:w-32 sm:h-32" />
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">1:1 with {advisorName}</h3>
            <p className="text-white/70 text-sm sm:text-base">Video session in progress...</p>
          </div>
        </div>

        {/* User Video Preview (Small corner) */}
        <div className="absolute top-4 right-4 w-24 h-32 sm:w-32 sm:h-40 bg-gray-800 rounded-xl border-2 border-white/20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center mx-auto mb-2">
              <span className="text-white font-bold text-sm">You</span>
            </div>
            <VideoIcon className="w-4 h-4 text-white/60 mx-auto" />
          </div>
        </div>
      </div>

      {/* Video Call Controls */}
      <div className="flex items-center justify-center gap-4 p-6 bg-black/80 backdrop-blur-sm border-t border-white/10">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="p-3 sm:p-4 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
          title="Mute/Unmute"
        >
          <Mic className="w-5 h-5 sm:w-6 sm:h-6" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="p-3 sm:p-4 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
          title="Toggle Video"
        >
          <VideoIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onEndCall}
          className="p-3 sm:p-4 rounded-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white transition-all duration-300"
          title="End Call"
        >
          <PhoneOff className="w-5 h-5 sm:w-6 sm:h-6" />
        </motion.button>
      </div>
    </motion.div>
  );
};